import { isDraggableGridShapeType } from './addMoveMode';
import {
  isHistoryRestoredIncompleteHaloProperty,
  markHistoryRestoredIncompleteShape,
} from './historyRestoredIncompleteHalo';
import type { GridHistoryButtonState } from './undoRedoButton';

export interface GridHistoryObject {
  t?: string;
  second?: unknown;
  [key: string]: unknown;
}

type HistoryObjectRef = Record<string, unknown> | unknown[];

const DERIVED_HISTORY_OBJECT_KEYS = new Set(['path', 'ar1', 'ar2']);

export interface GridHistoryState {
  objs: GridHistoryObject[];
  currentObjIndex?: number;
  shadePoint?: unknown;
  lineModeIndex?: number;
  lineModeMode?: string;
}

interface GridArgs {
  o?: {
    u?: GridHistoryObject[];
  };
  shadePoint?: unknown;
}

interface GridLike {
  args: GridArgs;
  currentObj?: GridHistoryObject;
  shadePoint?: unknown;
  clearCurrentMaybeSave: () => void;
}

interface GridGuideLike {
  on: boolean;
  dot?: unknown;
  currentObj?: unknown;
  close: () => void;
}

interface GridLineModeLike {
  i: number;
  mode?: string;
  states: string[];
}

interface CreateGridGetterHistoryOptions {
  grid: GridLike;
  getLineMode: () => GridLineModeLike | undefined;
  getGridGuide: () => GridGuideLike | undefined;
  paintCanvas: () => void;
  changedMaybe: () => void;
  beforeHistoryChange?: () => void;
  rehydrateHistoryObject?: (obj: GridHistoryObject) => void;
  restoreIncompleteShapeMode?: (mode: string) => void;
  maxHistoryEntries?: number;
}

export interface GridGetterHistoryController {
  undoStack: GridHistoryState[];
  redoStack: GridHistoryState[];
  maxHistoryEntries: number;
  setUndoButton: (button?: GridHistoryButtonState) => void;
  setRedoButton: (button?: GridHistoryButtonState) => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
  cloneGridStateForHistory: (
    includeIncompleteCurrentShape?: boolean
  ) => GridHistoryState;
  gridHistoryStatesEqual: (a: GridHistoryState, b: GridHistoryState) => boolean;
  applyGridStateFromHistory: (state: GridHistoryState) => void;
  updateUndoRedoButtons: () => void;
  clearIncompleteCurrentShape: (repaint?: boolean) => boolean;
  cancelIncompleteGridInteraction: (repaint?: boolean) => boolean;
  recordGridInteraction: (startState?: GridHistoryState) => boolean;
  recordIncompleteGridInteraction: (startState?: GridHistoryState) => boolean;
  recordCoalescedGridInteraction: (
    startState?: GridHistoryState,
    coalesceKey?: unknown
  ) => boolean;
  undoGridInteraction: () => boolean;
  redoGridInteraction: () => boolean;
}

const cloneValue = <T>(value: T): T => {
  if (value == null || typeof value !== 'object') {
    return value;
  }

  const seen = new WeakMap<HistoryObjectRef, unknown>();

  const cloneRecursive = (source: unknown): unknown => {
    if (source == null || typeof source !== 'object') {
      return source;
    }

    const sourceObject = source as HistoryObjectRef;
    const seenValue = seen.get(sourceObject);

    if (seenValue) {
      return seenValue;
    }

    if (Array.isArray(source)) {
      const clonedArray: unknown[] = [];

      seen.set(source, clonedArray);

      source.forEach(item => {
        clonedArray.push(cloneRecursive(item));
      });

      return clonedArray;
    }

    const sourceRecord = source as Record<string, unknown>;
    const clonedObject: Record<string, unknown> = {};

    seen.set(sourceRecord, clonedObject);

    Object.keys(sourceRecord).forEach(key => {
      if (
        key === 'line' ||
        DERIVED_HISTORY_OBJECT_KEYS.has(key) ||
        isHistoryRestoredIncompleteHaloProperty(key)
      ) {
        return;
      }

      clonedObject[key] = cloneRecursive(sourceRecord[key]);
    });

    return clonedObject;
  };

  return cloneRecursive(value) as T;
};

const isIncompleteHistoryObject = (
  obj: GridHistoryObject | undefined
): boolean => Boolean(obj && isDraggableGridShapeType(obj.t) && !obj.second);

const isIncompleteShape = (grid: GridLike): boolean =>
  isIncompleteHistoryObject(grid.currentObj);

const clearGridGuide = (gridGuide: GridGuideLike | undefined): void => {
  if (!gridGuide) {
    return;
  }

  gridGuide.close();
  gridGuide.dot = undefined;
  gridGuide.currentObj = undefined;
};

const areHistoryValuesEqual = (a: unknown, b: unknown): boolean => {
  const seenPairs = new WeakMap<HistoryObjectRef, WeakSet<HistoryObjectRef>>();

  const areEqual = (left: unknown, right: unknown): boolean => {
    if (left === right) {
      return true;
    }

    if (
      left == null ||
      right == null ||
      typeof left !== 'object' ||
      typeof right !== 'object'
    ) {
      return false;
    }

    const leftObject = left as HistoryObjectRef;
    const rightObject = right as HistoryObjectRef;

    let rightObjectsSeenForLeft = seenPairs.get(leftObject);

    if (!rightObjectsSeenForLeft) {
      rightObjectsSeenForLeft = new WeakSet<HistoryObjectRef>();
      seenPairs.set(leftObject, rightObjectsSeenForLeft);
    } else if (rightObjectsSeenForLeft.has(rightObject)) {
      return true;
    }

    rightObjectsSeenForLeft.add(rightObject);

    if (Array.isArray(left) || Array.isArray(right)) {
      if (!Array.isArray(left) || !Array.isArray(right)) {
        return false;
      }

      if (left.length !== right.length) {
        return false;
      }

      return left.every((value, index) => areEqual(value, right[index]));
    }

    const leftRecord = left as Record<string, unknown>;
    const rightRecord = right as Record<string, unknown>;

    const leftKeys = Object.keys(leftRecord);
    const rightKeys = Object.keys(rightRecord);

    if (leftKeys.length !== rightKeys.length) {
      return false;
    }

    return leftKeys.every(
      key =>
        Object.prototype.hasOwnProperty.call(rightRecord, key) &&
        areEqual(leftRecord[key], rightRecord[key])
    );
  };

  return areEqual(a, b);
};

export const createGridGetterHistory = (
  options: CreateGridGetterHistoryOptions
): GridGetterHistoryController => {
  const {
    grid,
    getLineMode,
    getGridGuide,
    paintCanvas,
    changedMaybe,
    beforeHistoryChange,
    rehydrateHistoryObject,
    restoreIncompleteShapeMode,
    maxHistoryEntries = 50,
  } = options;

  const undoStack: GridHistoryState[] = [];
  const redoStack: GridHistoryState[] = [];

  let undoButton: GridHistoryButtonState | undefined;
  let redoButton: GridHistoryButtonState | undefined;
  let coalescedInteractionStartState: GridHistoryState | undefined;
  let coalescedInteractionKey: unknown;

  const canUndo = (): boolean =>
    undoStack.length > 0 || isIncompleteShape(grid);
  const canRedo = (): boolean => redoStack.length > 0;

  const cloneGridStateForHistoryInternal = (
    includeIncompleteCurrentShape = false
  ): GridHistoryState => {
    const lineMode = getLineMode();
    const objects = grid.args.o?.u || [];
    const historyObjects: GridHistoryObject[] = [];
    let currentObjIndex: number | undefined;

    objects.forEach(obj => {
      const isCurrentIncomplete =
        includeIncompleteCurrentShape &&
        isIncompleteShape(grid) &&
        obj === grid.currentObj;

      if (isIncompleteHistoryObject(obj) && !isCurrentIncomplete) {
        return;
      }

      if (isCurrentIncomplete) {
        currentObjIndex = historyObjects.length;
      }

      historyObjects.push(cloneValue(obj));
    });

    // In-progress line/ray/segment/parabola objects are intentionally dropped
    // from normal snapshots so committed history stays stable.
    return {
      objs: historyObjects,
      currentObjIndex,
      shadePoint: cloneValue(grid.args.shadePoint),
      lineModeIndex: lineMode ? lineMode.i : undefined,
      lineModeMode: lineMode
        ? lineMode.mode ?? lineMode.states[lineMode.i]
        : undefined,
    };
  };

  const cloneGridStateForHistory = (
    includeIncompleteCurrentShape = false
  ): GridHistoryState =>
    cloneGridStateForHistoryInternal(includeIncompleteCurrentShape);

  const gridHistoryStatesEqual = (
    a: GridHistoryState,
    b: GridHistoryState
  ): boolean =>
    a.currentObjIndex === b.currentObjIndex &&
    a.lineModeIndex === b.lineModeIndex &&
    a.lineModeMode === b.lineModeMode &&
    // Shade direction is recomputed from the committed objects, so it does not
    // need to create a distinct history entry by itself.
    areHistoryValuesEqual(a.objs, b.objs);

  const updateUndoRedoButtons = (): void => {
    if (undoButton) {
      undoButton.hasHistory = canUndo();
    }

    if (redoButton) {
      redoButton.hasHistory = canRedo();
    }
  };

  const clearCoalescedGridInteraction = (): void => {
    coalescedInteractionStartState = undefined;
    coalescedInteractionKey = undefined;
  };

  const clearRedoStack = (): void => {
    redoStack.splice(0, redoStack.length);
    updateUndoRedoButtons();
  };

  const pushUndoState = (state: GridHistoryState): void => {
    undoStack.push(state);

    if (undoStack.length > maxHistoryEntries) {
      undoStack.splice(0, undoStack.length - maxHistoryEntries);
    }

    clearRedoStack();
  };

  const pushUndoStateFromRedo = (state: GridHistoryState): void => {
    undoStack.push(state);

    if (undoStack.length > maxHistoryEntries) {
      undoStack.splice(0, undoStack.length - maxHistoryEntries);
    }
  };

  const restoreGridStateFromHistory = (state: GridHistoryState): void => {
    if (!grid.args.o) {
      grid.args.o = {};
    }

    grid.args.o.u = cloneValue(state.objs || []);

    if (rehydrateHistoryObject) {
      grid.args.o.u.forEach(obj => {
        try {
          rehydrateHistoryObject(obj);
        } catch (_error) {
          void _error;
        }
      });
    }

    if (state.shadePoint) {
      grid.shadePoint = cloneValue(state.shadePoint);
      grid.args.shadePoint = cloneValue(state.shadePoint);
    } else {
      grid.shadePoint = undefined;
      grid.args.shadePoint = undefined;
    }

    const lineMode = getLineMode();

    if (lineMode && state.lineModeIndex != null) {
      lineMode.i = state.lineModeIndex;
      lineMode.mode =
        state.lineModeMode != null
          ? state.lineModeMode
          : lineMode.states[state.lineModeIndex];
    }

    const currentObj =
      state.currentObjIndex != null
        ? grid.args.o.u[state.currentObjIndex]
        : undefined;

    grid.currentObj = isIncompleteHistoryObject(currentObj)
      ? currentObj
      : undefined;

    markHistoryRestoredIncompleteShape(grid.currentObj);

    if (
      restoreIncompleteShapeMode &&
      isIncompleteHistoryObject(grid.currentObj) &&
      grid.currentObj?.t
    ) {
      restoreIncompleteShapeMode(grid.currentObj.t);
    }

    clearGridGuide(getGridGuide());
    paintCanvas();
  };

  const applyGridStateFromHistory = (state: GridHistoryState): void => {
    beforeHistoryChange?.();
    restoreGridStateFromHistory(state);
  };

  const clearIncompleteCurrentShape = (repaint = true): boolean => {
    if (!isIncompleteShape(grid)) {
      return false;
    }

    clearCoalescedGridInteraction();
    grid.clearCurrentMaybeSave();
    clearGridGuide(getGridGuide());
    updateUndoRedoButtons();

    if (repaint) {
      paintCanvas();
    }

    return true;
  };

  const cancelIncompleteGridInteraction = (repaint = true): boolean => {
    if (!clearIncompleteCurrentShape(repaint)) {
      return false;
    }

    clearRedoStack();
    changedMaybe();

    return true;
  };

  const recordGridInteraction = (startState?: GridHistoryState): boolean => {
    clearCoalescedGridInteraction();

    if (!startState) {
      return false;
    }

    const endState = cloneGridStateForHistory();

    if (gridHistoryStatesEqual(startState, endState)) {
      return false;
    }

    pushUndoState(startState);

    return true;
  };

  const recordIncompleteGridInteraction = (
    startState?: GridHistoryState
  ): boolean => {
    clearCoalescedGridInteraction();

    if (!startState || !isIncompleteShape(grid)) {
      return false;
    }

    const endState = cloneGridStateForHistoryInternal(true);

    if (gridHistoryStatesEqual(startState, endState)) {
      return false;
    }

    clearRedoStack();

    return true;
  };

  const recordCoalescedGridInteraction = (
    startState?: GridHistoryState,
    coalesceKey?: unknown
  ): boolean => {
    if (!startState) {
      return false;
    }

    const endState = cloneGridStateForHistory();
    const interactionChanged = !gridHistoryStatesEqual(startState, endState);

    if (!interactionChanged) {
      return false;
    }

    let activeStartState = startState;

    if (
      coalesceKey != null &&
      coalescedInteractionKey === coalesceKey &&
      coalescedInteractionStartState
    ) {
      activeStartState = coalescedInteractionStartState;
    } else if (coalesceKey != null) {
      coalescedInteractionStartState = startState;
      coalescedInteractionKey = coalesceKey;
    } else {
      clearCoalescedGridInteraction();
    }

    const lastUndoState = undoStack[undoStack.length - 1];
    const hasCoalescedStartState =
      lastUndoState && gridHistoryStatesEqual(lastUndoState, activeStartState);

    if (gridHistoryStatesEqual(activeStartState, endState)) {
      if (hasCoalescedStartState) {
        undoStack.pop();
      }

      clearRedoStack();

      return true;
    }

    if (hasCoalescedStartState) {
      clearRedoStack();

      return true;
    }

    pushUndoState(activeStartState);

    return true;
  };

  const undoGridInteraction = (): boolean => {
    beforeHistoryChange?.();
    clearCoalescedGridInteraction();

    if (isIncompleteShape(grid)) {
      const currentState = cloneGridStateForHistoryInternal(true);

      if (clearIncompleteCurrentShape()) {
        redoStack.push(currentState);
        updateUndoRedoButtons();
        changedMaybe();

        return true;
      }
    }

    if (undoStack.length === 0) {
      return false;
    }

    const currentState = cloneGridStateForHistory();
    const previousState = undoStack.pop();

    if (!previousState) {
      return false;
    }

    redoStack.push(currentState);
    restoreGridStateFromHistory(previousState);
    updateUndoRedoButtons();
    changedMaybe();

    return true;
  };

  const redoGridInteraction = (): boolean => {
    beforeHistoryChange?.();
    clearCoalescedGridInteraction();

    if (redoStack.length === 0) {
      return false;
    }

    const currentState = cloneGridStateForHistory(isIncompleteShape(grid));

    clearIncompleteCurrentShape(false);

    const nextState = redoStack.pop();

    if (!nextState) {
      return false;
    }

    if (nextState.currentObjIndex == null) {
      pushUndoStateFromRedo(currentState);
    }

    restoreGridStateFromHistory(nextState);
    updateUndoRedoButtons();
    changedMaybe();

    return true;
  };

  return {
    undoStack,
    redoStack,
    maxHistoryEntries,
    setUndoButton: button => {
      undoButton = button;
      updateUndoRedoButtons();
    },
    setRedoButton: button => {
      redoButton = button;
      updateUndoRedoButtons();
    },
    canUndo,
    canRedo,
    cloneGridStateForHistory,
    gridHistoryStatesEqual,
    applyGridStateFromHistory,
    updateUndoRedoButtons,
    clearIncompleteCurrentShape,
    cancelIncompleteGridInteraction,
    recordGridInteraction,
    recordIncompleteGridInteraction,
    recordCoalescedGridInteraction,
    undoGridInteraction,
    redoGridInteraction,
  };
};
